/**
 * Destacados, al estilo de la estrella de Gmail.
 *
 * Vive en `localStorage`, no en el servidor, y eso es una limitación real que
 * conviene tener presente: lo que destaques aquí no te sigue a otro navegador
 * ni lo ve el resto del equipo. Se hace así porque no hay columna donde
 * guardarlo —ningún registro del backend tiene un campo de destacado— y añadir
 * una toca la API, el worker y la base de datos de cada tipo de registro.
 *
 * Es exactamente el mismo trato que ya recibe el punto de «no leído» en
 * [[dashboardUnread]], así que al menos las dos señales de la lista se
 * comportan igual en vez de una de cada forma.
 */
export type DashboardStarResource = 'contacts' | 'forms' | 'pages' | 'collections' | 'cms-items' | 'offers'

function storageKey(projectId: string, resource: DashboardStarResource): string {
  return `veltica:dashboard-starred:${projectId}:${resource}`
}

export function readStarredIds(projectId: string, resource: DashboardStarResource): Set<string> {
  if (typeof window === 'undefined') return new Set()
  try {
    // `JSON.parse` devuelve `any`, y ese `any` se colaba hasta el `Set` sin
    // que nada volviera a comprobarlo. Anotarlo `unknown` obliga a estrechar
    // aquí, que es donde ya estaba la comprobación de verdad.
    const value: unknown = JSON.parse(window.localStorage.getItem(storageKey(projectId, resource)) ?? '[]')
    if (!Array.isArray(value)) return new Set()
    return new Set((value as unknown[]).filter((id): id is string => typeof id === 'string'))
  } catch {
    return new Set()
  }
}

/** Devuelve el conjunto resultante para que quien llama repinte sin releer. */
export function setStarred(
  projectId: string,
  resource: DashboardStarResource,
  ids: string[],
  starred: boolean,
): Set<string> {
  const next = readStarredIds(projectId, resource)
  for (const id of ids) {
    if (starred) next.add(id)
    else next.delete(id)
  }
  if (typeof window === 'undefined') return next
  try {
    // El mismo tope que los vistos: una bandeja con mucho tráfico no puede
    // hacer crecer el almacenamiento sin límite.
    window.localStorage.setItem(storageKey(projectId, resource), JSON.stringify([...next].slice(-500)))
  } catch {
    // Sin almacenamiento la estrella no persiste, pero la lista sigue yendo.
  }
  return next
}
